import { useEffect, useRef, useState } from "react";
import type { ArtifactFilters, Visibility } from "../lib/api";

const TAG_RE = /^[a-z0-9-]{1,30}$/;

/** Two-row toolbar above the artifacts grid. */
interface Props {
  /** Current filter state (URL-synced upstream). */
  filters: ArtifactFilters;
  /** Whether to surface the admin-only "Soft-deleted" status option. */
  isAdmin: boolean;
  /** Called whenever any field changes. Search input is debounced internally. */
  onChange: (next: ArtifactFilters) => void;
}

/** Debounced controlled input that mirrors ``filters.q`` upstream. */
function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  // Re-sync when the upstream value changes (e.g., after URL navigation).
  useEffect(() => setDraft(value), [value]);
  // Debounce keystrokes by 250ms before notifying upstream.
  useEffect(() => {
    if (draft === value) return;
    const id = window.setTimeout(() => onChange(draft), 250);
    return () => window.clearTimeout(id);
  }, [draft, value, onChange]);

  return (
    <div className="relative min-w-[240px] flex-1">
      <span
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400"
        aria-hidden
      >
        🔍
      </span>
      <input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Search title, description, slug…"
        className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-9 pr-3 text-sm placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        aria-label="Search artifacts"
      />
    </div>
  );
}

/** A small <select> styled as a pill, bound to one filter field. */
function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | "";
  options: { value: T | ""; label: string }[];
  onChange: (next: T | "") => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-sm text-slate-600 focus-within:border-slate-500">
      <span className="text-xs uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | "")}
        className="border-0 bg-transparent pr-1 text-sm focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Toolbar. Two rows: search + scope, then filter dropdowns + sort. */
export function ArtifactsToolbar({ filters, isAdmin, onChange }: Props) {
  const update = (patch: Partial<ArtifactFilters>) =>
    onChange({ ...filters, ...patch, offset: 0 });

  const [tagInputOpen, setTagInputOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const [tagDraftError, setTagDraftError] = useState("");
  const tagAddingRef = useRef(false);

  function commitTagDraft() {
    if (tagAddingRef.current) return;
    tagAddingRef.current = true;
    const val = tagDraft.trim();
    if (!val) {
      setTagInputOpen(false);
      setTagDraft("");
      tagAddingRef.current = false;
      return;
    }
    if (!TAG_RE.test(val)) {
      setTagDraftError("lowercase, a–z 0–9 hyphens, max 30 chars");
      tagAddingRef.current = false;
      return;
    }
    const current = filters.tags ?? [];
    if (!current.includes(val)) {
      onChange({ ...filters, tags: [...current, val], offset: 0 });
    }
    setTagDraft("");
    setTagDraftError("");
    setTagInputOpen(false);
    tagAddingRef.current = false;
  }

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: search + scope tabs */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={filters.q ?? ""} onChange={(q) => update({ q })} />
        <div
          role="tablist"
          aria-label="Scope"
          className="inline-flex rounded-md border border-slate-300 bg-white p-0.5"
        >
          {(
            [
              ["mine", "Mine"],
              ["all", "All"],
              ["shared_with_me", "Shared with me"],
            ] as const
          ).map(([s, label]) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={filters.scope === s}
              onClick={() => update({ scope: s })}
              className={`rounded px-3 py-1 text-sm font-medium ${
                filters.scope === s
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Row 2: filter dropdowns + sort */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterDropdown<Visibility>
          label="Visibility"
          value={filters.visibility ?? ""}
          options={[
            { value: "", label: "Any" },
            { value: "private", label: "Private" },
            { value: "unlisted", label: "Unlisted" },
            { value: "shared", label: "Shared" },
          ]}
          onChange={(visibility) =>
            update({ visibility: visibility || undefined })
          }
        />
        <FilterDropdown
          label="Status"
          value={filters.status ?? "active"}
          options={[
            { value: "active", label: "Active" },
            { value: "expiring", label: "Expiring < 7d" },
            { value: "permanent", label: "Permanent" },
            ...(isAdmin
              ? [{ value: "deleted" as const, label: "Soft-deleted" }]
              : []),
          ]}
          onChange={(status) => update({ status: status || "active" })}
        />
        {/* Owner is only meaningful when scope=all */}
        {filters.scope === "all" && (
          <label className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-sm text-slate-600 focus-within:border-slate-500">
            <span className="text-xs uppercase tracking-wide text-slate-400">
              Owner
            </span>
            <input
              type="text"
              value={filters.owner ?? ""}
              onChange={(e) =>
                update({ owner: e.target.value || undefined })
              }
              placeholder="email@datacamp.com"
              className="w-44 border-0 bg-transparent text-sm placeholder-slate-400 focus:outline-none"
              aria-label="Owner email filter"
            />
          </label>
        )}
        <FilterDropdown
          label="Sort"
          value={filters.sort ?? "updated"}
          options={[
            { value: "updated", label: "Updated" },
            { value: "created", label: "Created" },
            { value: "views", label: "Most viewed" },
            { value: "title", label: "Title" },
          ]}
          onChange={(sort) => update({ sort: sort || "updated" })}
        />
        {!(filters.tags?.length) && (
          <button
            type="button"
            onClick={() => {
              setTagInputOpen(true);
              setTagDraftError("");
            }}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-violet-300 px-3 py-1 text-xs text-violet-600 hover:border-violet-500"
          >
            + Tag filter
          </button>
        )}
      </div>

      {/* Row 3: active tag chips */}
      {(tagInputOpen || (filters.tags?.length ?? 0) > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-violet-50 px-3 py-2">
          <span className="mr-1 text-xs uppercase tracking-wide text-slate-400">
            Tags:
          </span>
          {(filters.tags ?? []).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2 py-0.5 text-xs text-white"
            >
              {tag}
              <button
                type="button"
                onClick={() => {
                  const next = (filters.tags ?? []).filter((t) => t !== tag);
                  onChange({
                    ...filters,
                    tags: next.length ? next : undefined,
                    offset: 0,
                  });
                  if (!next.length) setTagInputOpen(false);
                }}
                className="leading-none opacity-70 hover:opacity-100"
                aria-label={`Remove tag filter ${tag}`}
              >
                ×
              </button>
            </span>
          ))}
          {(filters.tags?.length ?? 0) < 10 &&
            (tagInputOpen ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  type="text"
                  value={tagDraft}
                  onChange={(e) => {
                    setTagDraft(e.target.value);
                    setTagDraftError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitTagDraft();
                    }
                    if (e.key === "Escape") {
                      setTagInputOpen(false);
                      setTagDraft("");
                      setTagDraftError("");
                    }
                  }}
                  onBlur={commitTagDraft}
                  placeholder="e.g. bi-tool"
                  className={`w-28 rounded border px-1.5 py-0.5 text-xs focus:outline-none ${
                    tagDraftError
                      ? "border-red-400 focus:ring-1 focus:ring-red-400"
                      : "border-violet-300 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                  }`}
                />
                {tagDraftError && (
                  <span className="text-xs text-red-500">{tagDraftError}</span>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setTagInputOpen(true);
                  setTagDraftError("");
                }}
                className="rounded-full border border-dashed border-violet-300 px-2 py-0.5 text-xs text-violet-600 hover:border-violet-500"
              >
                + add
              </button>
            ))}
          <button
            type="button"
            onClick={() => {
              onChange({ ...filters, tags: undefined, offset: 0 });
              setTagInputOpen(false);
              setTagDraft("");
            }}
            className="ml-auto text-xs text-slate-400 hover:text-slate-600"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
